import './index.scss';

import React from 'react';
import { injectIntl, defineMessages } from 'react-intl';
import classnames from 'classnames';
import throttle from 'lodash.throttle';

import { closeNotice } from 'hellospa/common/actions/condor';
import { ContextProvider as CondorContextProvider, withCondorContext } from 'hellospa/common/providers/condor';

import { useHSUser } from 'hellospa/common/hs-user';
import { useSiteCode } from 'js/sign-components/common/use-site-code';
import { useUnsafePageProps } from 'hellospa/slices/page-props';
import CondorHeader from './header';
import CondorFooter from './footer';
import CondorSidebar from './sidebar';
import CondorNoticeBar from './notice-bar';

export const { Provider: CondorLayoutProvider, Consumer } = React.createContext();

const MOBILE_MEDIA_QUERY = '(max-width: 767px), (max-width: 900px) and (min-aspect-ratio: 16/9)';

const messages = defineMessages({
  closeSidebarLabel: {
    id: '', // IDs are procedurally generated
    description: 'The aria label for a button which closes the sidebar.',
    defaultMessage: 'Close sidebar',
  },
});

class CondorLayout extends React.Component {

  state = {
    isSidebarOpen: false,
    isMobile: false,
  };

  baseRef = React.createRef();

  onWindowResize = throttle(() => this.updateIsMobile(), 50);

  onDocumentClick = (evt) => {
    if (this.state.isSidebarOpen) {
      if (!evt.target.closest('.condor-layout__sidebar')) {
        // The user has clicked somewhere other than within
        // the sidebar.
        this.closeSidebar();
      }
    }
  };

  static defaultProps = {
    isSidebar: true,
    isLogoutOnly: false,
    isUserMenu: true,
  };

  componentDidMount() {
    window.addEventListener('resize', this.onWindowResize);

    // Initial run.
    this.updateIsMobile();
  }

  componentDidUpdate(prevProps, prevState) {
    if (this.state.isSidebarOpen !== prevState.isSidebarOpen) {
      if (this.state.isSidebarOpen) {
        document.addEventListener('click', this.onDocumentClick);
      } else {
        document.removeEventListener('click', this.onDocumentClick);
      }
    }
  }

  componentWillUnmount() {
    window.removeEventListener('resize', this.onWindowResize);

    if (this.isSidebarOpen) {
      document.removeEventListener('click', this.onDocumentClick);
    }
  }

  updateIsMobile() {
    const isMobile = window.matchMedia(MOBILE_MEDIA_QUERY).matches;

    this.setState({
      isMobile,
    });
  }

  openSidebar = () => {
    this.setState({
      isSidebarOpen: true,
    });
  };

  closeSidebar = () => {
    this.setState({
      isSidebarOpen: false,
    });
  };

  onRequestCloseNotice = (id) => {
    this.props.condorContext.dispatch(closeNotice(id));
  };

  render() {
    const { isSidebarOpen, isMobile } = this.state;
    const {
      intl,
      condorContext,
      layoutContext,
      sidebar,
      isSidebar,
      isLogoutOnly,
      isUserMenu,
      user,
      siteCode,
      pageProps,
    } = this.props;
    const { termsURL, privacyURL } = layoutContext;
    const { permissions } = layoutContext.pageProps;
    const { currentUserRole, currentUserTeam, currentUserRootTeam } = pageProps;

    const extendedLayoutContext = {
      ...layoutContext,
    };

    if (currentUserTeam && currentUserRootTeam) {
      const isUserAdminOrManagerOfRootTeam = currentUserTeam.guid === currentUserRootTeam.guid;

      extendedLayoutContext.isUserAdminOrManagerOfRootTeam = isUserAdminOrManagerOfRootTeam;
    }

    if (currentUserRole) {
      extendedLayoutContext.roleCode = currentUserRole.code;
    }

    if (currentUserTeam) {
      extendedLayoutContext.teamName = currentUserTeam.name;
    }

    const classes = classnames('condor-layout', {
      'condor-layout--is-sidebar-enabled': isSidebar,
      'condor-layout--is-sidebar-open': isSidebarOpen,
    });

    return (
      <div ref={this.baseRef} className={classes}>
        <CondorNoticeBar
          notices={condorContext.state.notices}
          onRequestCloseNotice={this.onRequestCloseNotice} />
        {isSidebar && (
          <div className="condor-layout__sidebar">
            {isMobile && (
              <div
                className="condor-layout__sidebar-close-button"
                onClick={this.closeSidebar}
                role="button"
                aria-label={intl.formatMessage(messages.closeSidebarLabel)}
              >
                <span className="condor-layout__sidebar-close" />
              </div>
            )}
            <CondorSidebar
              {...sidebar}
              siteCode={siteCode}
              permissions={permissions} />
          </div>
        )}
        <div className="condor-layout__content">
          <div className={classnames(this.props.condorLayoutMain, 'condor-layout__main')}>
            {isMobile && <div className="condor-layout__screen" />}
            <div className="condor-layout__header">
              <CondorHeader
                user={user}
                siteCode={siteCode}
                isMobile={isMobile}
                isLogoutOnly={isLogoutOnly}
                isUserMenu={isUserMenu}
                isSidebar={isSidebar}
                openSidebar={this.openSidebar}
                condorContext={condorContext} />
            </div>
            <div className="condor-layout__body">
              {this.props.children({ layoutContext: extendedLayoutContext })}
            </div>
          </div>
          <div className="condor-layout__footer">
            <CondorFooter
              termsURL={termsURL}
              privacyURL={privacyURL}
              condorFooter={this.props.condorFooter} />
          </div>
        </div>
      </div>
    );
  }
}

const CondorLayoutWithContext = withCondorContext(injectIntl(CondorLayout));

export default function CondorLayoutWrapper(props) {
  const user = useHSUser();
  const siteCode = useSiteCode();
  const pageProps = useUnsafePageProps();

  return (
    <Consumer>
      {(layoutContext) => (
        <CondorContextProvider>
          <CondorLayoutWithContext
            {...props}
            siteCode={siteCode}
            user={user}
            pageProps={pageProps}
            layoutContext={layoutContext} />
        </CondorContextProvider>
      )}
    </Consumer>
  );
}
